import type { NaveOrganizationSummary, NavePlatformContext, NaveSessionBootstrap, NaveGrantSummary } from "../../read-models/identity/nave-session-bootstrap";
export type { NaveOrganizationSummary, NavePlatformContext, NaveSessionBootstrap, NaveGrantSummary } from "../../read-models/identity/nave-session-bootstrap";
export type NaveSessionClient = Readonly<{
  read(): Promise<NaveSessionBootstrap | null>;
  logout(): Promise<void>;
  switchOrganization(organizationId: string): Promise<void>;
  switchClient?(clientId: string): Promise<void>;
}>;

/** Same-origin BFF client. It receives only customer-safe session state, never provider tokens. */
export const createNaveSessionClient = (request: typeof fetch = fetch): NaveSessionClient => ({
  async read() {
    const response = await request("/api/session", {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("SESSION_UNAVAILABLE");
    const body: unknown = await response.json();
    if (typeof body !== "object" || body === null || Array.isArray(body)
      || typeof (body as { authenticated?: unknown }).authenticated !== "boolean") {
      throw new Error("INVALID_SESSION_RESPONSE");
    }
    if (!(body as { authenticated: boolean }).authenticated) return null;
    const session = body as Partial<NaveSessionBootstrap>;
    if (!session.actor || typeof session.actor.id !== "string" || typeof session.actor.displayName !== "string"
      || (session.actor.avatarRef !== undefined && session.actor.avatarRef !== null && typeof session.actor.avatarRef !== "string")
      || !Array.isArray(session.organizations) || !Array.isArray(session.grants) || typeof session.expiresAt !== "string"
      || (session.activeOrganization !== undefined && !session.organizations.some((organization) => organization.id === session.activeOrganization?.id))) {
      throw new Error("INVALID_SESSION_RESPONSE");
    }
    if (session.organizations.some((organization) => !isOrganizationSummary(organization))) throw new Error("INVALID_SESSION_RESPONSE");
    if (session.grants.some((grant) => !isGrantSummary(grant))) throw new Error("INVALID_SESSION_RESPONSE");
    if (session.platform !== undefined && !isNavePlatformContext(session.platform)) throw new Error("INVALID_SESSION_RESPONSE");
    if (session.platform?.homeOrganizationId && !session.organizations.some((organization) => organization.id === session.platform?.homeOrganizationId)) throw new Error("INVALID_SESSION_RESPONSE");
    if (session.clients !== undefined && (!Array.isArray(session.clients) || session.clients.some(c=>
      typeof c.id!=="string" || typeof c.name!=="string" || typeof c.isTest!=="boolean"))) throw new Error("INVALID_SESSION_RESPONSE");
    if(session.activeClient && !session.clients?.some(c=>c.id===session.activeClient?.id)) throw new Error("INVALID_SESSION_RESPONSE");
    return session as NaveSessionBootstrap;
  },
  async logout() {
    const response = await request("/auth/logout", { method: "POST", credentials: "same-origin" });
    if (!response.ok) throw new Error("SESSION_LOGOUT_UNAVAILABLE");
  },
  async switchClient(clientId) {
    const response=await request("/api/session/client",{method:"POST",credentials:"same-origin",cache:"no-store",
      headers:{"Content-Type":"application/json",Accept:"application/json"},body:JSON.stringify({clientId})});
    if(!response.ok) throw new Error(response.status===403 ? "CLIENT_FORBIDDEN":"CLIENT_SWITCH_UNAVAILABLE");
  },
  async switchOrganization(organizationId) {
    const response = await request("/api/session/organization", {
      method: "POST", credentials: "same-origin", cache: "no-store",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ organizationId }),
    });
    if (!response.ok) throw new Error(response.status === 403 ? "ORGANIZATION_FORBIDDEN" : "ORGANIZATION_SWITCH_UNAVAILABLE");
  },
});

const isOrganizationSummary = (organization: unknown): organization is NaveOrganizationSummary =>
  typeof organization === "object" && organization !== null && !Array.isArray(organization)
  && typeof (organization as NaveOrganizationSummary).id === "string"
  && typeof (organization as NaveOrganizationSummary).name === "string"
  && typeof (organization as NaveOrganizationSummary).slug === "string"
  && (organization as NaveOrganizationSummary).status === "ACTIVE"
  && ((organization as NaveOrganizationSummary).customerNames === undefined || (Array.isArray((organization as NaveOrganizationSummary).customerNames) && (organization as NaveOrganizationSummary).customerNames!.every((name) => typeof name === "string")))
  && ((organization as NaveOrganizationSummary).membershipRole === undefined || typeof (organization as NaveOrganizationSummary).membershipRole === "string")
  && ((organization as NaveOrganizationSummary).selectionAuthority === undefined || ["MEMBERSHIP", "PLATFORM_ADMIN"].includes(String((organization as NaveOrganizationSummary).selectionAuthority)))
  && typeof (organization as NaveOrganizationSummary).isDefault === "boolean";

const isGrantSummary = (grant: unknown): grant is NaveGrantSummary =>
  typeof grant === "object" && grant !== null && !Array.isArray(grant)
  && typeof (grant as NaveGrantSummary).organizationId === "string"
  && typeof (grant as NaveGrantSummary).capability === "string"
  && ((grant as NaveGrantSummary).scopeKind === "organization" || (grant as NaveGrantSummary).scopeKind === "resource")
  && typeof (grant as NaveGrantSummary).scopeRef === "string";

export const isNavePlatformContext = (value: unknown): value is NavePlatformContext => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const platform = value as Partial<NavePlatformContext>;
  const isAdmin = platform.isPlatformAdmin;
  const capabilities = platform.capabilities;
  return typeof isAdmin === "boolean" && Array.isArray(capabilities)
    && capabilities.every((capability) => ["nave.organizations.list", "nave.organizations.select", "nave.customer.read", "nave.clients.list", "nave.clients.select"].includes(capability))
    && (platform.homeOrganizationId === null || typeof platform.homeOrganizationId === "string")
    && (isAdmin || capabilities.length === 0);
};
